/**
 * Zentrale Notification-Service-Klasse
 * Central Notification Service Class
 * 
 * Bietet eine zentrale API für die Erstellung und Verwaltung von Benachrichtigungen
 * Provides a central API for creating and managing notifications
 */

import { 
  getCategoryLabel, 
  getUITranslation, 
  getMessageTranslation, 
  getTitleTranslation 
} from '../utils/notificationTranslations';

class NotificationService {
  constructor() {
    this.currentLanguage = 'fr';
  }

  /**
   * Setzt die aktuelle Sprache
   * Sets the current language
   */
  setLanguage(language) {
    if (['fr', 'ar'].includes(language)) {
      this.currentLanguage = language;
    } else {
      console.warn(`Unsupported language: ${language}. Defaulting to 'fr'`);
      this.currentLanguage = 'fr';
    }
  }

  /**
   * Ruft die aktuelle Sprache ab
   * Gets the current language
   */
  getLanguage() {
    return this.currentLanguage;
  }

  /**
   * Ruft die Kategorie-Bezeichnung ab
   * Gets the category label
   */
  getCategoryLabel(category) {
    return getCategoryLabel(category, this.currentLanguage);
  }

  /**
   * Erstellt eine Notification für ein abgelaufenes Produkt
   * Creates a notification for an expired product
   */
  createExpiredProductNotification(productName, batchNumber, quantity, lotId, expirationDate, location) {
    return {
      title: getTitleTranslation('productExpired', this.currentLanguage),
      message: getMessageTranslation('productExpired', this.currentLanguage, productName, batchNumber, quantity),
      type: 'error',
      category: 'EXPIRED',
      persistent: true,
      data: {
        productName,
        batchNumber,
        quantity,
        lotId,
        expirationDate,
        location,
        eventKey: `expired_lot_${lotId}`
      }
    };
  }

  /**
   * Erstellt eine Notification für ein bald ablaufendes Produkt
   * Creates a notification for a product expiring soon
   */
  createExpiringProductNotification(productName, batchNumber, daysUntilExpiry, quantity, lotId, expirationDate, location) {
    return {
      title: getTitleTranslation('productExpiring', this.currentLanguage),
      message: getMessageTranslation('productExpiring', this.currentLanguage, productName, batchNumber, daysUntilExpiry, quantity),
      type: 'warning',
      category: 'EXPIRING',
      persistent: true,
      data: {
        productName,
        batchNumber,
        daysUntilExpiry,
        quantity,
        lotId,
        expirationDate,
        location,
        eventKey: `expiring_lot_${lotId}`
      }
    };
  }

  /**
   * Erstellt eine Notification für Nachbestellungsbedarf
   * Creates a notification for reorder needs
   */
  createReorderNotification(productName, stock, productId, reorderPoint, avgDailyDemand) {
    return {
      title: getTitleTranslation('reorderNeeded', this.currentLanguage),
      message: getMessageTranslation('reorderNeeded', this.currentLanguage, productName, stock),
      type: 'warning',
      category: 'REORDER',
      persistent: true,
      data: {
        productId,
        productName,
        currentStock: stock,
        reorderPoint,
        avgDailyDemand,
        eventKey: `reorder_${productId}`
      }
    };
  }

  /**
   * Erstellt eine Notification für Lieferaktualisierungen
   * Creates a notification for delivery updates
   */
  createDeliveryNotification(orderId, daysUntilDelivery, productName, expectedDeliveryDate, status) {
    return {
      title: getTitleTranslation('deliveryUpdate', this.currentLanguage),
      message: getMessageTranslation('deliveryUpdate', this.currentLanguage, orderId, daysUntilDelivery),
      type: 'info',
      category: 'DELIVERY',
      persistent: true,
      data: {
        orderId,
        productName,
        expectedDeliveryDate,
        daysUntilDelivery,
        status,
        eventKey: `delivery_${orderId}_${expectedDeliveryDate}`
      }
    };
  }

  /**
   * Erstellt eine Systemfehler-Notification
   * Creates a system error notification
   */
  createSystemErrorNotification(errorMessage, context) {
    return {
      title: getTitleTranslation('systemError', this.currentLanguage),
      message: getMessageTranslation('systemError', this.currentLanguage),
      type: 'error',
      category: 'GENERAL',
      persistent: false,
      data: {
        error: errorMessage,
        context
      }
    };
  }

  /**
   * Erstellt eine generische Notification
   * Creates a generic notification
   */
  createGenericNotification(title, message, type = 'info', category = 'GENERAL', persistent = false, data = {}) {
    return {
      title,
      message,
      type,
      category,
      persistent,
      data
    };
  }

  /**
   * Formatiert ein Datum relativ zur aktuellen Zeit
   * Formats a date relative to current time
   */
  formatRelativeDate(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return getUITranslation('now', this.currentLanguage);
    } else if (diffInHours < 24) {
      return getUITranslation('hoursAgo', this.currentLanguage, diffInHours);
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return getUITranslation('daysAgo', this.currentLanguage, diffInDays);
    }
  }

  /**
   * Ruft UI-Übersetzungen ab
   * Gets UI translations
   */
  getUI(key, ...params) {
    return getUITranslation(key, this.currentLanguage, ...params);
  }
}

// Singleton-Instanz
let notificationServiceInstance = null;

/**
 * Ruft die Singleton-Instanz des NotificationService ab
 * Gets the singleton instance of NotificationService
 */
export const getNotificationService = () => {
  if (!notificationServiceInstance) {
    notificationServiceInstance = new NotificationService();
  }
  return notificationServiceInstance;
};

/**
 * Initialisiert den NotificationService mit einer Sprache
 * Initializes the NotificationService with a language
 */
export const initializeNotificationService = (language) => {
  const service = getNotificationService();
  service.setLanguage(language);
  return service;
};

export default NotificationService;

